/**
 * useLevelFunnelPrefetch - Прогрев кэша остальных режимов «Воронок уровней»
 * ============================================================================
 *
 * По образцу useUsersSectionPrefetch.js, но с двумя отличиями, диктуемыми
 * ценой запроса (план 2026-09-11-level-funnels-speedup.md, C4):
 *  - режимы дёргаются ПОСЛЕДОВАТЕЛЬНО, а не все разом — каждый скан
 *    campaign/elite до ~2.5 GiB, параллельный залп по 3 режимам сразу
 *    после захода на вкладку не нужен;
 *  - прогрев стартует только когда активный режим уже пришёл (после него
 *    остальные три), и полностью отменяется при смене фильтров/размонтировании.
 *
 * Гейт по JSON.stringify({dateRange, platform}) — тем же полям, что реально
 * попадают в URL getLevelFunnelDataset (см. metricsService.js), иначе смена
 * несущественного фильтра (проект/кампания) заново перезапускала бы прогрев.
 */

import { useEffect, useRef } from 'react';
import { useFilters } from './useFilters';
import { MetricsService } from '../services/metricsService';
import { withDefaultDateRange } from '../utils/defaultDateRange';

const ALL_MODES = ['campaign', 'elite', 'absorption', 'td'];

function relevantFiltersKey(filters) {
  return JSON.stringify({
    startDate: filters?.dateRange?.startDate ?? filters?.startDate ?? null,
    endDate: filters?.dateRange?.endDate ?? filters?.endDate ?? null,
    platform: filters?.platform ?? null
  });
}

/**
 * @param {boolean} isActive - Вкладка «Воронки уровней» смонтирована и видна
 * @param {string} activeMode - Уже загруженный режим — прогреваем остальные
 */
export function useLevelFunnelPrefetch(isActive, activeMode) {
  const { filters } = useFilters();
  const lastKeyRef = useRef(null);

  useEffect(() => {
    if (!isActive) return undefined;

    const key = `${relevantFiltersKey(filters)}::${activeMode}`;
    if (lastKeyRef.current === key) return undefined;
    lastKeyRef.current = key;

    let cancelled = false;
    // Сигнал обрывает и УЖЕ ЗАПУЩЕННЫЙ запрос, а не только цикл между
    // итерациями: скан campaign/elite дорогой, и долетать до ClickHouse после
    // смены фильтров ему незачем. Тот же приём, что в useWidgetData.
    const controller = new AbortController();
    const scoped = withDefaultDateRange(filters);
    const modesToWarm = ALL_MODES.filter((m) => m !== activeMode);

    (async () => {
      for (const mode of modesToWarm) {
        if (cancelled || controller.signal.aborted) return;
        try {
          await MetricsService.getLevelFunnelDataset(scoped, mode, {
            signal: controller.signal
          });
        } catch {
          // Прогрев best-effort: ошибка (и отмена) одного режима не должна
          // прерывать остальные — цикл сам остановится по проверке выше.
        }
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [isActive, activeMode, filters]);
}

export default useLevelFunnelPrefetch;
